import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/shared/services/supabase';
import { formatDate } from '@/shared/utils/dateUtils';
import type { Location } from '@/shared/types/domain.types';
import type { DateRangePreset } from '../hooks/useReportFilters';

interface StickyDateRangeHeaderProps {
  dateFrom: string;
  dateTo: string;
  datePreset: DateRangePreset;
  locationIds: string[];
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  custom: 'Custom Range',
};

export function StickyDateRangeHeader({
  dateFrom,
  dateTo,
  datePreset,
  locationIds,
}: StickyDateRangeHeaderProps) {
  // Fetch location names if locations are filtered
  const { data: locations } = useQuery({
    queryKey: ['locations', locationIds],
    queryFn: async () => {
      if (locationIds.length === 0) return [];
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, short_code')
        .in('id', locationIds)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Pick<Location, 'id' | 'name' | 'short_code'>[];
    },
    enabled: locationIds.length > 0,
  });

  const getDateRangeDisplay = () => {
    if (datePreset !== 'custom') {
      return PRESET_LABELS[datePreset];
    }
    return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
  };

  const getLocationDisplay = () => {
    if (locationIds.length === 0) {
      return 'All Locations';
    }
    if (locations && locations.length > 0) {
      if (locations.length === 1) {
        return locations[0].name;
      }
      if (locations.length <= 3) {
        return locations.map((l) => l.name).join(', ');
      }
      return `${locations.length} locations selected`;
    }
    return `${locationIds.length} location(s)`;
  };

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 sm:px-6 py-3 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Date Range:</span>
            <span className="text-gray-900">{getDateRangeDisplay()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Locations:</span>
            <span className="text-gray-900">{getLocationDisplay()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


